import { Steps, Button, Spin } from 'antd';
import { Header } from '../../../../../core/components';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StepsProfile from '../components/stepsProfile';
import StepsCompanyProfile from '../components/stepsCompanyProfile';
import StepsClients from './stepsClients';
import StepsUsers from './stepsUsers';
import { ProfileService } from '../../profile/service/profile.service';
import { useRouteStore } from '../../../../../core/store/route.store';
import { User } from '../../profile/model/user.model';
import { route } from '../../../../../constants/routes';

interface WizardStepsProps {
  onComplete?: () => void;
}

const WizardSteps: React.FC<WizardStepsProps> = ({ onComplete }) => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(() => {
  const savedStep = localStorage.getItem('wizardCurrentStep');
  return savedStep ? Number(savedStep) : 0;
  });

  const { setRoutePrefix } = useRouteStore();
  const [profile, setProfile] = useState<User | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
// const [isInitialSetup, setIsInitialSetup] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const wizardInProgress = localStorage.getItem('wizardInProgress') === 'true';
  const wizardComplete = localStorage.getItem('wizardComplete') === 'true';

  const refreshProfile = async () => {
    setLoading(true);
    try {
      const updatedProfile = await ProfileService.Get();
      setProfile(updatedProfile);
      setCompanyId(updatedProfile.companyId || null);
    } catch (error) {
      console.error('Error refreshing profile:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const getProfile = async () => {
      try {
        const profile = await ProfileService.Get();
        if (!profile) throw new Error('Profile data is missing');
        setProfile(profile);
        setCompanyId(profile.companyId || null);

        if (profile.admin && !wizardComplete) localStorage.setItem('wizardInProgress', 'true');
         
         
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    getProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardComplete]);


  useEffect(() => {
    localStorage.setItem('wizardCurrentStep', current.toString());
  }, [current]);


  if (loading || !profile) {
    return (
      <div className='min-h-screen flex justify-center items-center'>
        <Spin size='large' />
      </div>
    );
  }


  const showAllSteps = profile.admin === true && (wizardInProgress || profile.companySet === false) && !wizardComplete;

  function isProfileComplete() {
    return profile?.firstname && profile?.lastname && profile?.email && profile?.phoneNumber;
  }

  function isStepValid() {
    if (!profile) return false;
    switch (current) {
      case 0:
        return isProfileComplete();
      case 1:
        return companyId && companyId !== '';
      case 2:
        return true;
      case 3:
        return true;
      default:
        return true;
    }
  }

  async function next() {
    if (isStepValid()) {
      if (current === 0 && profile) {
        try {
          await ProfileService.Patch({ ...profile, profileSet: false });
          refreshProfile();
        } catch (error) {
          console.error('Error updating profile:', error);
          return;
        }
      }
      setCurrent((prev) => prev + 1);
    }
  }

  function prev() {
    setCurrent((prev) => prev - 1);
  }

  const handleCompanyUpdated = (updatedCompanyId: string) => {
    setCompanyId(updatedCompanyId);
    refreshProfile();
  };

 async function goToDashboard() {
  if (profile && isProfileComplete()) {
    setLoading(true); 
    try {
      await ProfileService.Patch({
        ...profile,
        profileSet: true,
        companySet: true,
      });

      const updatedProfile = await ProfileService.Get();
      setProfile(updatedProfile);
      setCompanyId(updatedProfile.companyId || null);

      localStorage.setItem('userProfile', JSON.stringify(updatedProfile));
      localStorage.setItem('wizardComplete', 'true');
      localStorage.removeItem('wizardInProgress');
      localStorage.setItem('hasJustFinishedWizard', 'true');
      localStorage.removeItem('wizardCurrentStep');

      if (onComplete) {
        onComplete();
      } else {
        setRoutePrefix(updatedProfile?.companyName || '/admin');
        navigate(route('/dashboard'));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  }
}
  return (
    <div className='min-h-screen content-center'>
      {initialLoading ? (
        <div className='flex justify-center items-center p-4'>
          <Spin size='large' />
        </div>
      ) : (
        <div className='p-6 md:p-0 w-full max-w-xl md:max-w-2xl lg:max-w-3xl xl:max-w-4xl mx-auto'>
          <Header title='Setup Your Account' buttons={[]} />
          <Steps current={current} className='mt-4 md:mt-10'>
            <Steps.Step title='User Profile' />
            {showAllSteps && (
              <>
                <Steps.Step title='Company Profile' />
                <Steps.Step title='Add Clients' />
                <Steps.Step title='Invite Users' />
              </>
            )}
            <Steps.Step title='Finish' />
          </Steps>
          <div>
            {current === 0 && <StepsProfile onProfileUpdated={refreshProfile} />}
            {showAllSteps && current === 1 && (<StepsCompanyProfile onCompanyUpdated={handleCompanyUpdated}/>)}
            {showAllSteps && current === 2 && <StepsClients />}
            {showAllSteps && current === 3 && <StepsUsers />}
            {current === (showAllSteps ? 4 : 1) && (
              <div className='flex flex-col my-14 space-y-4 text-center '>
                <h2 className='text-2xl font-semibold mb-4'>Setup Complete!</h2>
                <p className='mb-6'>
                  You’ve successfully finished the setup. Click below to go to your dashboard.
                </p>
              </div>
            )}

            <div className='flex space-x-4 justify-center mt-6'>
              <Button onClick={prev} disabled={current === 0}>
                Previous
              </Button>
              {current < (showAllSteps ? 4 : 1) ? (
                <Button type='primary' onClick={next} disabled={!isStepValid()}>
                  Next
                </Button>
              ) : (
                <Button type='primary' onClick={goToDashboard} disabled={!isProfileComplete()}>
                  Go to Dashboard
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WizardSteps;
